import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { getAccessToken } from '../utils/authSession';

const API = '/api/symptoms';

function AdminSymptomBrowse() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [hidingKey, setHidingKey] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadAll();
  }, [navigate]);

  const loadAll = async () => {
    const token = getAccessToken();
    const headers = { Authorization: `Bearer ${token}` };
    try {
      setLoading(true);
      setError('');
      const aggRes = await axios.get(`${API}/browse-catalog`, { headers });
      const list = aggRes.data?.symptoms || [];
      setSymptoms(list);
      const initialDrafts = {};
      for (const s of list) {
        initialDrafts[s.name] = s.description || '';
      }
      setDrafts(initialDrafts);
    } catch (e) {
      if (e.response?.status === 401) navigate('/admin/login');
      console.error(e);
      setError(e.response?.data?.error || 'Failed to load symptoms');
      setSymptoms([]);
    } finally {
      setLoading(false);
    }
  };

  const visibleRows = useMemo(
    () => symptoms.filter((s) => !s.excluded),
    [symptoms]
  );

  const hiddenRows = useMemo(
    () => symptoms.filter((s) => s.excluded),
    [symptoms]
  );

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const base = visibleRows;
    if (!q) return base;
    return base.filter((s) => s.name.toLowerCase().includes(q));
  }, [visibleRows, filter]);

  const handleDraftChange = (name, value) => {
    setDrafts((d) => ({ ...d, [name]: value }));
  };

  const handleSave = async (symptomName) => {
    const token = getAccessToken();
    try {
      setSavingKey(symptomName);
      setError('');
      setSuccessMessage('');
      await axios.put(
        `${API}/browse-descriptions`,
        { symptom_name: symptomName, description: drafts[symptomName] ?? '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const v = (drafts[symptomName] ?? '').trim();
      setSymptoms((prev) =>
        prev.map((x) =>
          x.name === symptomName ? { ...x, description: v || null } : x
        )
      );
      // In-page message: window.alert() is often blocked after async (await) — no longer a "user gesture".
      setSuccessMessage('Symptoms saved successfully.');
      window.setTimeout(() => setSuccessMessage(''), 8000);
    } catch (e) {
      if (e.response?.status === 401) navigate('/admin/login');
      setError(e.response?.data?.error || 'Save failed');
    } finally {
      setSavingKey(null);
    }
  };

  const handleHideFromBrowse = async (symptomName) => {
    if (!window.confirm(`Hide "${symptomName}" from the public symptom browse? Disease text is not changed.`)) {
      return;
    }
    const token = getAccessToken();
    try {
      setHidingKey(symptomName);
      setError('');
      await axios.post(
        `${API}/browse-exclusions`,
        { symptom_name: symptomName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSymptoms((prev) =>
        prev.map((x) =>
          x.name === symptomName ? { ...x, excluded: true, description: null } : x
        )
      );
      setDrafts((d) => ({ ...d, [symptomName]: '' }));
    } catch (e) {
      if (e.response?.status === 401) navigate('/admin/login');
      setError(e.response?.data?.error || 'Could not hide phrase');
    } finally {
      setHidingKey(null);
    }
  };

  const handleRestore = async (symptomName) => {
    const token = getAccessToken();
    try {
      setHidingKey(symptomName);
      setError('');
      await axios.delete(`${API}/browse-exclusions`, {
        data: { symptom_name: symptomName },
        headers: { Authorization: `Bearer ${token}` }
      });
      setSymptoms((prev) =>
        prev.map((x) =>
          x.name === symptomName ? { ...x, excluded: false } : x
        )
      );
    } catch (e) {
      if (e.response?.status === 401) navigate('/admin/login');
      setError(e.response?.data?.error || 'Could not restore phrase');
    } finally {
      setHidingKey(null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container-fluid py-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <h1 className="h3 mb-0">Symptom browse (public)</h1>
            <p className="text-muted small mb-0">
              Names come from disease articles. Use <strong>Hide from browse</strong> for lines that are sentences or
              headings, not symptom labels. <code>**</code> is stripped automatically. Hidden items can be restored
              below; they stay in disease content until you edit those articles.
            </p>
          </div>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={loadAll}>
            Refresh list
          </button>
        </div>

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="status">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              aria-label="Dismiss"
              onClick={() => setSuccessMessage('')}
            />
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3" style={{ maxWidth: '24rem' }}>
          <input
            type="search"
            className="form-control"
            placeholder="Filter visible symptoms..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {visibleRows.length === 0 && hiddenRows.length === 0 ? (
          <p className="text-muted">No symptoms found. Add symptom lists to disease articles first.</p>
        ) : (
          <div className="table-responsive mb-5">
            <h2 className="h5 mb-3">Shown on public browse</h2>
            <table className="table table-sm table-hover align-middle">
              <thead>
                <tr>
                  <th style={{ minWidth: '12rem' }}>Symptom</th>
                  <th>Description</th>
                  <th style={{ width: '11rem' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.name}>
                    <td className="fw-medium">{s.name}</td>
                    <td>
                      <textarea
                        className="form-control form-control-sm"
                        rows={3}
                        value={drafts[s.name] ?? ''}
                        onChange={(e) => handleDraftChange(s.name, e.target.value)}
                        placeholder="Optional short description for visitors..."
                      />
                    </td>
                    <td className="text-nowrap">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm me-1"
                        disabled={savingKey === s.name}
                        onClick={() => handleSave(s.name)}
                      >
                        {savingKey === s.name ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        disabled={hidingKey === s.name}
                        onClick={() => handleHideFromBrowse(s.name)}
                        title="Remove from A–Z browse only"
                      >
                        {hidingKey === s.name ? '…' : 'Hide'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && visibleRows.length > 0 && (
              <p className="text-muted small">No rows match the filter.</p>
            )}
          </div>
        )}

        {hiddenRows.length > 0 && (
          <div className="table-responsive border-top pt-4">
            <h2 className="h5 mb-2">Hidden from public browse</h2>
            <p className="text-muted small mb-3">
              These phrases no longer appear in letter search or lists. Restore to show them again.
            </p>
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Phrase</th>
                  <th style={{ width: '8rem' }} />
                </tr>
              </thead>
              <tbody>
                {hiddenRows.map((s) => (
                  <tr key={s.name}>
                    <td className="text-muted">{s.name}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm"
                        disabled={hidingKey === s.name}
                        onClick={() => handleRestore(s.name)}
                      >
                        Restore
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminSymptomBrowse;
